import { useQuery, UseQueryResult } from "react-query";
import { OptionsFilter } from "../../@types";
import { ResponsePanelApiDefault } from "../../@types/panelTypes";
import api from "../../service/api";
import { Client } from "./useClients";
import { Order } from "./useOrdersV2";
import { Seller } from "./useSeller";

export type OrderConfirm = {
  id: number;
  confirmed?: boolean;
  confirmedAt?: Date;
  orderId: string;

  order: Order;
  client: Client;
  seller: Seller;
};
export type OrderConfirmAnalises = {
  totalAmount: number;
  totalQuantity: number;
  statuses: {
    confirmed: boolean;
    totalAmount: number;
    totalQuantity: number;
  }[];
};
export type OrderConfirmFilters = {
  cod: number;
  codClient: number;
  cnpj: string;
  codSeller: number;
  brand?: string;
  orderPeriod?: number;
  orderPresetDate?: number;
  orderSpecificDate?: Date;
  orderStartDate?: Date;
  orderEndDate?: Date;
};

export interface OrderFilterOptions {
  sellers: OptionsFilter[];
  brands: OptionsFilter[];
  colections: OptionsFilter[];
}

type GetOrderConfirmsResponse = {
  orderConfirmes: OrderConfirm[];
  totalCount: number;
};

interface OrderConfirmApiResponse extends ResponsePanelApiDefault {
  contents: OrderConfirm[];
}

export async function getOrderConfirmes(
  page: number,
  filter?: OrderConfirmFilters,
  registersPerPag?: number
): Promise<GetOrderConfirmsResponse> {
  const { data } = await api.get<OrderConfirmApiResponse>("/orders-confirmes", {
    params: {
      pageRequest: page - 1,
      limitRequest: registersPerPag,
      ...filter,
    },
  });

  return {
    totalCount: data.total,
    orderConfirmes: data.contents,
  };
}

export function useOrderConfirmes(
  page: number,
  filter?: OrderConfirmFilters,
  registersPerPag?: number
): UseQueryResult<GetOrderConfirmsResponse> {
  return useQuery(
    ["order-confirmes", page, filter, registersPerPag],
    () => getOrderConfirmes(page, filter, registersPerPag),
    {
      staleTime: 1000 * 60 * 10, // 10 Minutos
    }
  );
}

export async function getOneOrderConfirm(
  id?: string
): Promise<OrderConfirm | undefined> {
  if (!id) return undefined;

  const { data } = await api.get<OrderConfirm>(`orders-confirmes/${id}`);

  return data;
}

export function useOneOrderConfirme(
  id?: string
): UseQueryResult<OrderConfirm> | undefined {
  return useQuery(["order-confirm", id], () => getOneOrderConfirm(id), {
    staleTime: 1000 * 60 * 10, // 10 Minutos
  });
}

export async function getOrderFilters(filters?: OrderConfirmFilters) {
  const { data } = await api.get<OrderFilterOptions>(
    "/orders-confirmes/get-filters",
    {
      params: {
        ...filters,
      },
    }
  );

  return data;
}

export function useOrderFilters(
  filters?: OrderConfirmFilters
): UseQueryResult<OrderFilterOptions> {
  return useQuery(
    ["order-confirmes-filters", filters],
    () => getOrderFilters(filters),
    {
      staleTime: 1000 * 60 * 10, // 10 Minutos
    }
  );
}

export function useOrderConfirmesAnalises(
  filter?: OrderConfirmFilters
): UseQueryResult<OrderConfirmAnalises> {
  return useQuery(
    ["order-confirmes-analises", filter],
    () => getOneOrderConfirmAnalises(filter),
    {
      staleTime: 1000 * 60 * 10, // 10 Minutos
    }
  );
}

export async function getOneOrderConfirmAnalises(
  filter?: OrderConfirmFilters
): Promise<OrderConfirmAnalises | undefined> {
  const { data } = await api.get<OrderConfirmAnalises>(
    `/analytic/order-confirmation/`,

    { params: { ...filter } }
  );

  return data;
}
